import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Avatar,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import CloseIcon from '@mui/icons-material/Close';
import CircleIcon from '@mui/icons-material/Circle';
import api from '../services/api';
import socketService from '../services/socketService';
import { useAuth } from '../hooks/useAuth';

interface Message {
  text: string;
  isFromCustomer: boolean;
  senderName?: string;
  senderId?: {
    firstName: string;
    lastName: string;
  };
  createdAt: string;
}

interface ContactMessage {
  _id: string;
  name: string;
  email: string;
  status: 'open' | 'closed' | 'archived';
  lastContactDate?: string;
  lastMessage?: {
    text: string;
    isFromCustomer: boolean;
    createdAt: string;
  };
  unreadCount?: number;
  totalMessages?: number;
  createdAt: string;
  updatedAt: string;
}

interface ContactMessageDetail extends ContactMessage {
  messages: Message[];
}

const AdminChat = () => {
  const { user, token } = useAuth();
  const [messages, setMessages] = useState<ContactMessage[]>([]);
  const [selectedMessage, setSelectedMessage] = useState<ContactMessageDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'open' | 'closed' | 'archived'>('all');
  const [presenceStatus, setPresenceStatus] = useState<'available' | 'busy' | 'away' | 'offline'>('available');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Connect to Socket.IO for real-time updates
  useEffect(() => {
    if (token && user?.primaryRole === 'app_admin') {
      // For app admins, use empty string or 'admin' as agencySlug
      socketService.connect(token, 'admin').catch(err => {
        console.error('Failed to connect socket:', err);
      });
    }

    return () => {
      socketService.disconnect();
    };
  }, [token, user?.primaryRole]);

  const fetchMessages = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await api.get(`/admin/contact-messages${params}`);
      setMessages(response.data);
    } catch (err: any) {
      console.error('Error fetching messages:', err);
      setError(err.response?.data?.error || 'Failed to fetch messages');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  const fetchMessageDetail = async (contactMessageId: string, silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await api.get(`/admin/contact-messages/${contactMessageId}`);
      setSelectedMessage(response.data);
    } catch (err: any) {
      console.error('Error fetching message detail:', err);
      setError(err.response?.data?.error || 'Failed to fetch message details');
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  };

  // Fetch and update presence status
  useEffect(() => {
    const updatePresence = async (status: 'available' | 'busy' | 'away' | 'offline') => {
      try {
        await api.patch('/admin/presence', { status });
        setPresenceStatus(status);
        
        // Also emit via Socket.IO for real-time updates
        const socket = socketService.getSocket();
        if (socket && socket.connected) {
          socket.emit('update_presence', { status });
        }
      } catch (err: any) {
        console.error('Error updating presence:', err);
      }
    };

    // Set initial presence to available when component mounts and socket is connected
    if (token && user?.primaryRole === 'app_admin' && socketService.isSocketConnected()) {
      updatePresence('available');
    }

    // Update presence to offline when component unmounts
    return () => {
      if (token && user?.primaryRole === 'app_admin') {
        updatePresence('offline').catch(console.error);
      }
    };
  }, [token, user?.primaryRole]);

  // Listen for real-time contact message events
  useEffect(() => {
    if (!socketService.isSocketConnected()) return;

    const handleContactMessage = (data: any) => {
      console.log('📨 Received contact message event:', data);
      
      if (data.type === 'new_message' || data.type === 'admin_reply') {
        // Refresh messages list
        fetchMessages(true);
        
        // If the selected message matches, refresh its detail
        if (selectedMessage && data.contactMessageId === selectedMessage._id) {
          fetchMessageDetail(selectedMessage._id, true);
        } else if (data.type === 'new_message' && data.contactMessage) {
          // If it's a new message and we're viewing that thread, refresh it
          if (selectedMessage && selectedMessage._id === data.contactMessage._id) {
            fetchMessageDetail(selectedMessage._id, true);
          }
        }
      }
    };

    // Listen for contact_message events using socketService
    const unsubscribe = socketService.on('contact_message', handleContactMessage);
    
    return () => {
      unsubscribe();
    };
  }, [selectedMessage?._id, statusFilter]);

  useEffect(() => {
    fetchMessages();
  }, [statusFilter]);

  useEffect(() => {
    if (selectedMessage) {
      fetchMessageDetail(selectedMessage._id);
    }
  }, [selectedMessage?._id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedMessage?.messages]);

  const handleSendMessage = async () => {
    if (!selectedMessage || !newMessage.trim()) return;

    setSending(true);
    setError(null);

    try {
      const response = await api.post(`/admin/contact-messages/${selectedMessage._id}/messages`, {
        text: newMessage,
      });

      if (response.data.success) {
        setSelectedMessage(response.data.contactMessage);
        setNewMessage('');
        fetchMessages(); // Refresh list
      }
    } catch (err: any) {
      console.error('Error sending message:', err);
      setError(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const handleStatusChange = async (contactMessageId: string, status: 'open' | 'closed' | 'archived') => {
    try {
      const response = await api.patch(`/admin/contact-messages/${contactMessageId}/status`, {
        status,
      });

      if (response.data.success) {
        if (selectedMessage?._id === contactMessageId) {
          setSelectedMessage({ ...selectedMessage, status });
        }
        fetchMessages();
      }
    } catch (err: any) {
      console.error('Error updating status:', err);
      setError(err.response?.data?.error || 'Failed to update status');
    }
  };

  const formatTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString(navigator.language, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'primary';
      case 'closed':
        return 'default';
      case 'archived':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const handlePresenceChange = async (newStatus: 'available' | 'busy' | 'away' | 'offline') => {
    try {
      await api.patch('/admin/presence', { status: newStatus });
      setPresenceStatus(newStatus);
      
      // Also emit via Socket.IO for real-time updates
      const socket = socketService.getSocket();
      if (socket && socket.connected) {
        socket.emit('update_presence', { status: newStatus });
      }
    } catch (err: any) {
      console.error('Error updating presence:', err);
      setError(err.response?.data?.error || 'Failed to update presence');
    }
  };

  const getPresenceColor = (status: string) => {
    switch (status) {
      case 'available':
        return 'success';
      case 'busy':
        return 'error';
      case 'away':
        return 'warning';
      case 'offline':
        return 'default';
      default:
        return 'default';
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            Customer Messages
          </Typography>
          <Typography variant="body2" color="text.secondary">
            View and respond to messages from customers on the main page
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Status:
          </Typography>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={presenceStatus}
              onChange={(e) => handlePresenceChange(e.target.value as any)}
              startAdornment={
                <CircleIcon
                  sx={{
                    fontSize: 12,
                    mr: 1,
                    color: getPresenceColor(presenceStatus) === 'success' ? '#4caf50' :
                           getPresenceColor(presenceStatus) === 'error' ? '#f44336' :
                           getPresenceColor(presenceStatus) === 'warning' ? '#ff9800' : '#9e9e9e',
                  }}
                />
              }
            >
              <MenuItem value="available">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircleIcon sx={{ fontSize: 12, color: '#4caf50' }} />
                  Available
                </Box>
              </MenuItem>
              <MenuItem value="busy">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircleIcon sx={{ fontSize: 12, color: '#f44336' }} />
                  Busy
                </Box>
              </MenuItem>
              <MenuItem value="away">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircleIcon sx={{ fontSize: 12, color: '#ff9800' }} />
                  Away
                </Box>
              </MenuItem>
              <MenuItem value="offline">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircleIcon sx={{ fontSize: 12, color: '#9e9e9e' }} />
                  Offline
                </Box>
              </MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box sx={{ display: 'flex', gap: 2, height: 'calc(100vh - 250px)' }}>
        {/* Messages List */}
        <Paper sx={{ width: 350, display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value as any)}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="open">Open</MenuItem>
                <MenuItem value="closed">Closed</MenuItem>
                <MenuItem value="archived">Archived</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <List sx={{ flex: 1, overflow: 'auto' }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : messages.length === 0 ? (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                  No messages found
                </Typography>
              </Box>
            ) : (
              messages.map((msg) => (
                <ListItem
                  key={msg._id}
                  button
                  selected={selectedMessage?._id === msg._id}
                  onClick={() => setSelectedMessage(msg as any)}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    position: 'relative',
                  }}
                  secondaryAction={
                    msg.status === 'open' ? (
                      <Tooltip title="Close chat">
                        <IconButton
                          edge="end"
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStatusChange(msg._id, 'closed');
                          }}
                          sx={{ mr: 1 }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : null
                  }
                >
                  <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      {msg.name}
                    </Typography>
                    <Chip label={msg.status} size="small" color={getStatusColor(msg.status) as any} />
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5 }}>
                    {msg.email}
                  </Typography>
                  {msg.lastMessage && (
                    <Typography variant="body2" sx={{ mb: 0.5 }} noWrap>
                      {msg.lastMessage.text}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', mt: 0.5 }}>
                    <Typography variant="caption" color="text.secondary">
                      {formatTime(msg.lastContactDate || msg.updatedAt)}
                    </Typography>
                    {msg.unreadCount && msg.unreadCount > 0 && (
                      <Chip label={msg.unreadCount} size="small" color="primary" />
                    )}
                  </Box>
                </ListItem>
              ))
            )}
          </List>
        </Paper>

        {/* Chat Window */}
        <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {selectedMessage ? (
            <>
              <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6">{selectedMessage.name}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {selectedMessage.email}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {selectedMessage.status === 'open' && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<CloseIcon />}
                        onClick={() => handleStatusChange(selectedMessage._id, 'closed')}
                        color="inherit"
                      >
                        Close Chat
                      </Button>
                    )}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <Select
                        value={selectedMessage.status}
                        onChange={(e) => handleStatusChange(selectedMessage._id, e.target.value as any)}
                      >
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                        <MenuItem value="archived">Archived</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
              </Box>

              <Box
                ref={messagesContainerRef}
                sx={{
                  flex: 1,
                  overflow: 'auto',
                  p: 2,
                  bgcolor: '#f5f5f5',
                }}
              >
                {selectedMessage.messages?.map((msg, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: 'flex',
                      mb: 2,
                      flexDirection: msg.isFromCustomer ? 'row' : 'row-reverse',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Avatar
                      sx={{
                        bgcolor: msg.isFromCustomer ? '#90caf9' : '#b39ddb',
                        width: 32,
                        height: 32,
                        mr: msg.isFromCustomer ? 1 : 0,
                        ml: msg.isFromCustomer ? 0 : 1,
                      }}
                    >
                      {msg.isFromCustomer
                        ? msg.senderName?.[0]?.toUpperCase() || 'C'
                        : msg.senderId?.firstName?.[0]?.toUpperCase() || 'A'}
                    </Avatar>
                    <Box sx={{ maxWidth: '70%' }}>
                      <Paper
                        sx={{
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: msg.isFromCustomer ? '#fff' : '#1976d2',
                          color: msg.isFromCustomer ? '#000' : '#fff',
                        }}
                      >
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                          {msg.text}
                        </Typography>
                      </Paper>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        {msg.senderName || (msg.senderId && `${msg.senderId.firstName} ${msg.senderId.lastName}`)} ·{' '}
                        {formatTime(msg.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                ))}
                <div ref={messagesEndRef} />
              </Box>

              <Divider />
              <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Type a message..."
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                    disabled={sending || selectedMessage.status === 'archived'}
                  />
                  <Button
                    variant="contained"
                    onClick={handleSendMessage}
                    disabled={sending || !newMessage.trim() || selectedMessage.status === 'archived'}
                    startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
                  >
                    Send
                  </Button>
                </Box>
              </Box>
            </>
          ) : (
            <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
              <Typography variant="body1">Select a message to view and respond</Typography>
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default AdminChat;

